import { SearchIcon } from 'lucide-react'

import { Checkbox, FilterPillButton, FiltersDropdown, TextInput } from 'slab'
import { GetEmployersRenewalsRenewalConfigsIdEmployeesFilterEmploymentTypesItem as EmploymentTypeFilter, GetEmployersRenewalsRenewalConfigsIdEmployeesFilterEnrollmentStatusesItem as EnrollmentStatusFilter } from 'utils/web_api.schemas'

import { employmentTypeDisplayValue, enrollmentStatusDisplayValue } from './utils'
type Props = {
  search: string
  enrollmentStatuses: EnrollmentStatusFilter[]
  employmentTypes: EmploymentTypeFilter[]
  onSearchChange: (search: string) => void
  onEnrollmentStatusesChange: (enrollmentStatuses: EnrollmentStatusFilter[]) => void
  onEmploymentTypesChange: (employmentTypes: EmploymentTypeFilter[]) => void
  excludeOffboardedEmployees: boolean
  onExcludeOffboardedEmployeesChange: (excludeOffboardedEmployees: boolean) => void
  numOffboardedEmployees: number
  trailingComponent?: React.ReactNode
}

const EMPLOYMENT_TYPE_FILTERS = Object.values(EmploymentTypeFilter).map((employmentType) => {
  return {
    label: employmentTypeDisplayValue(employmentType),
    value: employmentType,
  }
}).sort((a, b) => a.label.localeCompare(b.label))

const ENROLLMENT_STATUS_FILTERS = Object.values(EnrollmentStatusFilter).map((enrollmentStatus) => {
  return {
    label: enrollmentStatusDisplayValue(enrollmentStatus),
    value: enrollmentStatus,
  }
}).sort((a, b) => a.label.localeCompare(b.label))

export default function RenewalEmployeesTableFilters ({
  search,
  enrollmentStatuses,
  employmentTypes,
  onSearchChange,
  onEnrollmentStatusesChange,
  onEmploymentTypesChange,
  excludeOffboardedEmployees,
  onExcludeOffboardedEmployeesChange,
  numOffboardedEmployees,
  trailingComponent,
}: Props) {
  const onEnrollmentStatusChange = (enrollmentStatus: EnrollmentStatusFilter) => {
    const updatedEnrollmentStatuses = enrollmentStatuses.includes(enrollmentStatus)
      ? enrollmentStatuses.filter((status) => status !== enrollmentStatus)
      : [...enrollmentStatuses, enrollmentStatus]
    onEnrollmentStatusesChange(updatedEnrollmentStatuses)
  }

  const onEmploymentTypeChange = (employmentType: EmploymentTypeFilter) => {
    const updatedEmploymentTypes = employmentTypes.includes(employmentType)
      ? employmentTypes.filter((type) => type !== employmentType)
      : [...employmentTypes, employmentType]
    onEmploymentTypesChange(updatedEmploymentTypes)
  }

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-row gap-4 items-center flex-wrap'>
        <div className='flex-1 min-w-48'>
          <TextInput
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder='Search your team'
            leadingIcon={SearchIcon}
          />
        </div>
        {trailingComponent && trailingComponent}
      </div>
      <div className='flex gap-2 flex-wrap'>
        <FiltersDropdown
          trigger={
            <FilterPillButton
              label='Employment type'
              count={employmentTypes.length}
            />
          }
        >
          <FiltersDropdown.Filters>
            {EMPLOYMENT_TYPE_FILTERS.map((employmentType) => (
              <Checkbox
                key={employmentType.value}
                value={employmentType.value}
                label={employmentType.label}
                checked={employmentTypes.includes(employmentType.value)}
                onChange={() => onEmploymentTypeChange(employmentType.value)}
              />
            ))}
          </FiltersDropdown.Filters>
        </FiltersDropdown>
        <FiltersDropdown
          trigger={
            <FilterPillButton
              label='2025 Status'
              count={enrollmentStatuses.length}
            />
          }
        >
          <FiltersDropdown.Filters>
            {ENROLLMENT_STATUS_FILTERS.map((enrollmentStatus) => (
              <Checkbox
                key={enrollmentStatus.value}
                value={enrollmentStatus.value}
                label={enrollmentStatus.label}
                checked={enrollmentStatuses.includes(enrollmentStatus.value)}
                onChange={() => onEnrollmentStatusChange(enrollmentStatus.value)}
              />
            ))}
          </FiltersDropdown.Filters>
        </FiltersDropdown>
        {numOffboardedEmployees > 0 && (
          <FilterPillButton
            label='Exclude offboarded employees'
            count={excludeOffboardedEmployees ? numOffboardedEmployees : 0}
            onClick={() => onExcludeOffboardedEmployeesChange(!excludeOffboardedEmployees)}
            hideChevron
          />
        )}
      </div>
    </div>
  )
}
